import { OverlayModule } from '@angular/cdk/overlay';
import { Component, computed, forwardRef, input, signal } from '@angular/core';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors } from '@angular/forms';
import { MibMoment } from '../../../foundations';
import { fieldDescribedBy } from '../../../internal/field-control.utils';
import { MibIconComponent } from '../../icons';
import { MibDatepickerBase } from '../date-picker-base.directive';
import { MibDatepickerPanelComponent } from '../date-picker-panel/date-picker-panel.component';
import { datePlaceholder, dayOf, formatDate, isoDay, parseDate } from '../picker.utils';

/** A date typed or picked from a month that opens under the field. Its value is the picked day as a `YYYY-MM-DD` string. */
@Component({
  selector: 'mib-datepicker',
  imports: [OverlayModule, MibIconComponent, MibDatepickerPanelComponent],
  templateUrl: './date-picker.component.html',
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => MibDatepickerComponent), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => MibDatepickerComponent), multi: true },
  ],
  host: { class: 'block' },
})
export class MibDatepickerComponent extends MibDatepickerBase<string | null> {
  /** Left unset, the locale's own order, such as `MM/DD/YYYY`. */
  readonly placeholder = input<string | undefined>(undefined);
  /** Accessible name of the panel. */
  readonly panelLabel = input('Choose a date');
  /** The id of the error or help message read with the field, added to any `aria-describedby` already set. */
  readonly mibFieldDescribedBy = input<string | null>(null);

  protected readonly describedBy = fieldDescribedBy(this.mibFieldDescribedBy);

  protected readonly value = signal<Date | null>(null);
  protected readonly text = signal('');
  protected readonly shownPlaceholder = computed(() => this.placeholder() ?? datePlaceholder(this.locale()));

  /** Writes a `Date`, an ISO string, a `YYYY-MM-DD` day or epoch milliseconds into the field, as a day. */
  writeValue(value: MibMoment | null | undefined): void {
    const day = dayOf(value);
    this.value.set(day);
    this.text.set(day ? formatDate(day, this.locale()) : '');
  }

  /** Raises `mibDatepickerParse`, `mibDatepickerMin` or `mibDatepickerMax`. */
  validate(): ValidationErrors | null {
    const text = this.text();
    if (text.trim() && parseDate(text, this.locale()) === undefined) return { mibDatepickerParse: { text } };
    const value = this.value();
    if (!value) return null;
    const min = this.minDay();
    const max = this.maxDay();
    if (min && value < min) return { mibDatepickerMin: { min: isoDay(min), actual: isoDay(value) } };
    if (max && value > max) return { mibDatepickerMax: { max: isoDay(max), actual: isoDay(value) } };
    return null;
  }

  protected openingDay(): Date | null {
    return this.value();
  }

  protected onInput(event: Event): void {
    const text = this.readField(event);
    this.text.set(text);
    /* The value moves only on an empty field or a whole date: a half-typed one keeps the last. */
    const day = parseDate(text, this.locale(), true);
    if (!text.trim()) this.commit(null);
    else if (day) {
      this.commit(day);
      this.follow(day);
    } else this.onValidatorChange();
  }

  protected onBlur(): void {
    this.settle();
    this.onLeave();
  }

  /* Enter settles the field as leaving it does, and confirms the value unless what was typed is not a date. */
  protected onEnter(): void {
    const wrong = !!this.text().trim() && parseDate(this.text(), this.locale()) === undefined;
    this.settle();
    this.onLeave();
    this.close(false);
    const value = this.value();
    if (!wrong) this.confirmed.emit(value && isoDay(value));
  }

  protected pick(day: Date): void {
    this.text.set(formatDate(day, this.locale()));
    this.commit(day);
    this.onTouched();
    this.close();
    this.confirmed.emit(isoDay(day));
  }

  /* Takes a date with a short year, then writes back the value the field holds. */
  private settle(): void {
    const typed = parseDate(this.text(), this.locale());
    if (typed && typed.getTime() !== this.value()?.getTime()) this.commit(typed);
    const value = this.value();
    if (value) this.text.set(formatDate(value, this.locale()));
    this.onValidatorChange();
  }

  private commit(day: Date | null): void {
    const previous = this.value();
    this.value.set(day);
    if (previous?.getTime() !== day?.getTime()) this.onChange(day && isoDay(day));
    this.onValidatorChange();
  }
}
